const chapter = {
  id: 'alcohols-phenols-ethers',
  slug: 'alcohols-phenols-ethers',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Alcohols, Phenols & Ethers',
        hindi: 'एल्कोहॉल, फ़िनॉल और ईथर',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification & Structure',
        hindi: 'वर्गीकरण और संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Alcohols** R–OH; classified by C bearing –OH: 1°, 2°, 3°.',
        hindi: '**अल्कोहल** R–OH; C बेयरिंग द्वारा वर्गीकृत -OH: 1°, 2°, 3°।',
            },
            {
              english: '**Phenols** Ar–OH (–OH attached to $sp^2$ ring C).',
        hindi: '**फिनोल्स** Ar-OH (-OH $sp^2$ रिंग C से जुड़ा हुआ है)।',
            },
            {
              english: '**Ethers** R–O–R\' (symmetrical or unsymmetrical).',
        hindi: '**ईथर** आर-ओ-आर\\\' (सममित या असममित)।',
            },
            {
              english: 'C–O–H angle ≈ 108.9° in methanol ($sp^3$ O); C–O–C in ether ≈ 111°.',
        hindi: 'मेथनॉल में C-O-H कोण ≈ 108.9° ($sp^3$ O); ईथर में C-O-C ≈ 111°।',
            },
            {
              english: 'Strong **H-bonding** in alcohols/phenols ⇒ unusually high BP vs comparable ethers/alkanes; ethers cannot self-H-bond (no O–H) so BP ≈ alkanes of similar mass.',
        hindi: 'alcohols/phenols में मजबूत **H-बॉन्डिंग** ⇒ असामान्य रूप से उच्च BP बनाम तुलनीय ethers/alkanes; ईथर स्व-एच-बंधन नहीं कर सकते (कोई ओ-एच नहीं) इसलिए बीपी ≈ समान द्रव्यमान के अल्केन्स।',
            },
            {
              english: 'Solubility in water $\\downarrow$ as alkyl chain grows (lower alcohols miscible).',
        hindi: 'जैसे-जैसे एल्काइल श्रृंखला बढ़ती है, पानी में घुलनशीलता $\\downarrow$ हो जाती है (मिश्रणीय अल्कोहल कम हो जाता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'preparation',
      title: {
        english: 'Preparation of Alcohols',
        hindi: 'एल्कोहॉल की तैयारी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'From **alkenes**: acid-catalysed hydration (Markovnikov); **oxymercuration–demercuration** (Hg(OAc)₂/H₂O then NaBH₄ — Markovnikov, no rearrangement); **hydroboration–oxidation** ($BH_3 \\cdot THF$ then $H_2O_2/OH^-$ — anti-Markovnikov, syn).',
        hindi: '**अल्केन्स** से: एसिड-उत्प्रेरित जलयोजन (मार्कोवनिकोव); **ऑक्सीमरक्यूरेशन-डीमर्क्यूरेशन** (Hg(OAc)₂/H₂O फिर NaBH₄ - मार्कोवनिकोव, कोई पुनर्व्यवस्था नहीं); **हाइड्रोबोरेशन-ऑक्सीकरण** ($BH_3 \\cdot THF$ फिर $H_2O_2/OH^-$ - एंटी-मार्कोवनिकोव, syn)।',
            },
            {
              english: 'From **carbonyl** (reduction): aldehyde → 1° alcohol; ketone → 2° alcohol; ester → 1° alcohol. Reagents: $LiAlH_4$ (strong, reduces –COOH and esters), $NaBH_4$ (mild, only aldehyde/ketone), $H_2/Ni$.',
        hindi: '**कार्बोनिल** (कमी) से: एल्डिहाइड → 1° अल्कोहल; कीटोन → 2° अल्कोहल; एस्टर → 1° अल्कोहल। अभिकर्मक: $LiAlH_4$ (मजबूत, -COOH और एस्टर को कम करता है), $NaBH_4$ (हल्का, केवल aldehyde/ketone), $H_2/Ni$।',
            },
            {
              english: 'From **Grignard**: HCHO → 1°; RCHO → 2°; R₂CO → 3°; ester (excess RMgX) → 3° with two identical R; epoxide → β-hydroxy.',
        hindi: '**ग्रिगनार्ड** से: HCHO → 1°; आरसीएचओ → 2°; R₂CO → 3°; एस्टर (अतिरिक्त आरएमजीएक्स) → दो समान आर के साथ 3°; एपॉक्साइड → β-हाइड्रॉक्सी।',
            },
            {
              english: 'From haloalkanes by aq. KOH (SN2/SN1).',
        hindi: 'एक्यू द्वारा हैलोऐल्केन से। कोह (SN2/SN1).',
            },
            {
              english: '**Phenol** prep: cumene process (cumene + O₂ → cumene hydroperoxide → phenol + acetone, dilute H₂SO₄); from chlorobenzene (NaOH, 623 K, 320 atm — Dow); from benzene sulphonic acid (NaOH fusion); from diazonium salt (warm with H₂O).',
        hindi: '**फिनोल** तैयारी: क्यूमीन प्रक्रिया (क्यूमीन + O₂ → क्यूमीन हाइड्रोपरॉक्साइड → फिनोल + एसीटोन, पतला H₂SO₄); क्लोरोबेंजीन से (NaOH, 623 K, 320 atm - Dow); बेंजीन सल्फोनिक एसिड (NaOH संलयन) से; डायज़ोनियम नमक से (H₂O के साथ गर्म)।',
            },
          ],
        },
      ],
    },
    {
      id: 'reactions-alcohols',
      title: {
        english: 'Reactions of Alcohols',
        hindi: 'एल्कोहॉल की अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Acidity: $H_2O > 1° > 2° > 3°$ (steric + +I destabilises RO⁻).',
        hindi: 'अम्लता: $H_2O > 1° > 2° > 3°$ (स्टेरिक + +I RO⁻ को अस्थिर करता है)।',
            },
            {
              english: 'With Na: $2R\\text{-OH} + 2Na \\to 2R\\text{-ONa} + H_2$.',
        hindi: 'Na के साथ: $2R\\text{-OH} + 2Na \\to 2R\\text{-ONa} + H_2$.',
            },
            {
              english: 'Esterification: $R\\text{-OH} + R\\text{-COOH} \\xrightleftharpoons{H^+} R\\text{COOR} + H_2O$ (reversible; remove water; mechanism: nucleophilic acyl substitution).',
        hindi: 'एस्टेरिफिकेशन: $R\\text{-OH} + R\\text{-COOH} \\xrightleftharpoons{H^+} R\\text{COOR} + H_2O$ (प्रतिवर्ती; पानी निकालें; तंत्र: न्यूक्लियोफिलिक एसाइल प्रतिस्थापन)।',
            },
            {
              english: 'With HX → R-X (Lucas test for 1°/2°/3°: ZnCl₂ + conc HCl; **3° turbid immediately, 2° in 5 min, 1° on heating**).',
        hindi: 'HX → R-X के साथ (1°/2°/3° के लिए लुकास परीक्षण: ZnCl₂ + सांद्र HCl; **3° तुरंत अशांत, 5 मिनट में 2°, गर्म करने पर 1°**)। सांद्र H₂SO₄ के साथ निर्जलीकरण: 3° (170°C) > 2° > 1° (अल्कोहल → एल्केन; सैत्ज़ेफ़)। निचले टी पर → ईथर (1° पसंदीदा)।',
            },
            {
              english: 'Dehydration with conc H₂SO₄: 3° (170°C) > 2° > 1° (alcohol → alkene; Saytzeff). At lower T → ether (1° preferred).',
        hindi: 'सांद्र H₂SO₄ के साथ निर्जलीकरण: 3° (170°C) > 2° > 1° (अल्कोहल → एल्केन; सैत्ज़ेफ़)। निचले टी पर → ईथर (1° पसंदीदा)।',
            },
            {
              english: 'Oxidation:',
        hindi: 'ऑक्सीकरण: • 1° → एल्डिहाइड → कार्बोक्जिलिक एसिड (पीसीसी एल्डिहाइड पर रुकता है; KMnO₄/K₂Cr₂O₇ एसिड में जाता है)।',
            },
            {
              english: '• 1° → aldehyde → carboxylic acid (PCC stops at aldehyde; KMnO₄/K₂Cr₂O₇ goes to acid).',
        hindi: '• 1° → एल्डिहाइड → कार्बोक्जिलिक एसिड (पीसीसी एल्डिहाइड पर रुकता है; KMnO₄/K₂Cr₂O₇ एसिड में जाता है)।',
            },
            {
              english: '• 2° → ketone.',
        hindi: '• 2° → कीटोन।',
            },
            {
              english: '• 3° resists oxidation (no α-H on C-OH); harsh conditions cleave C–C.',
        hindi: '• 3° ऑक्सीकरण का प्रतिरोध करता है (C-OH पर कोई α-H नहीं); कठोर परिस्थितियाँ C-C को तोड़ देती हैं।',
            },
            {
              english: 'Catalytic dehydrogenation over Cu, 573 K: 1° → aldehyde, 2° → ketone, 3° → alkene (loses H₂O instead).',
        hindi: 'Cu, 573 K पर उत्प्रेरक डिहाइड्रोजनेशन: 1° → एल्डिहाइड, 2° → कीटोन, 3° → एल्केन (इसके बजाय H₂O खो देता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'phenol-reactions',
      title: {
        english: 'Phenol — Acidity & Reactions',
        hindi: 'फ़िनॉल — अम्लता और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Acidity: $\\text{phenol} (pK_a\\approx 10) \\gg \\text{alcohol} (pK_a\\approx 16)$ — phenoxide stabilised by resonance.',
        hindi: 'अम्लता: $\\text{phenol} (pK_a\\approx 10) \\gg \\text{alcohol} (pK_a\\approx 16)$ - फेनोक्साइड अनुनाद द्वारा स्थिर।',
            },
            {
              english: 'EWG (NO₂, CN, COOH) at ortho/para increases acidity; EDG (CH₃, OCH₃) decreases.',
        hindi: 'ortho/para पर EWG (NO₂, CN, COOH) अम्लता बढ़ाता है; EDG (CH₃, OCH₃) कम हो जाता है। **पिक्रिक एसिड** ortho/para-ट्रिनिट्रोफेनॉल; बहुत अम्लीय ().',
            },
            {
              english: '**Picric acid** $= 2,4,6$-trinitrophenol; very acidic ($pK_a \\approx 0.4$).',
        hindi: '**पिक्रिक एसिड** $= 2,4,6$-ट्रिनिट्रोफेनॉल; बहुत अम्लीय ($pK_a \\approx 0.4$).',
            },
            {
              english: 'Reactions:',
        hindi: 'प्रतिक्रियाएं:',
            },
            {
              english: '• Bromination — phenol + Br₂ in water → **2,4,6-tribromophenol** (white ppt) without catalyst (–OH highly activating).',
        hindi: '• ब्रोमिनेशन - पानी में फिनोल + Br→ **2,4,6-ट्राइब्रोमोफेनॉल** (सफेद पीपीटी) उत्प्रेरक के बिना (-OH अत्यधिक सक्रिय)।',
            },
            {
              english: '• Nitration — dilute HNO₃ → o- and p-nitrophenol (separable by steam distillation: o-isomer is volatile due to **intramolecular H-bonding (chelation)**, p- has intermolecular).',
        hindi: '• नाइट्रेशन - पतला HNO₃ → ओ- और पी-नाइट्रोफेनॉल (भाप आसवन द्वारा अलग किया जा सकता है: ओ-आइसोमर ** इंट्रामोल्युलर एच-बॉन्डिंग (केलेशन) ** के कारण अस्थिर है, पी- में इंटरमॉलिक्युलर है)।',
            },
            {
              english: '• Kolbe-Schmitt: phenoxide + CO₂ (400 K, 7 atm) then H⁺ → salicylic acid (o-hydroxybenzoic acid; aspirin precursor).',
        hindi: '• कोल्बे-श्मिट: फेनोक्साइड + CO₂ (400 K, 7 एटीएम) फिर H⁺ → सैलिसिलिक एसिड (o-हाइड्रॉक्सीबेन्जोइक एसिड; एस्पिरिन अग्रदूत)।',
            },
            {
              english: '• Reimer-Tiemann: phenol + CHCl₃ + NaOH → salicylaldehyde (CHO at o-, via dichlorocarbene :CCl₂).',
        hindi: '• रीमर-टीमैन: फिनोल + CHCl₃ + NaOH → सैलिसिल्डिहाइड (CHO ओ- पर, डाइक्लोरोकार्बिन के माध्यम से:CCl₂)।',
            },
            {
              english: '• Liebermann nitroso test: phenol + HNO₂ → blue → red (with NaOH).',
        hindi: '• लिबरमैन नाइट्रोसो परीक्षण: फिनोल + HNO₂ → नीला → लाल (NaOH के साथ)।',
            },
            {
              english: '• FeCl₃: phenol gives violet colouration.',
        hindi: '• FeCl₃: फिनोल बैंगनी रंग देता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'ethers',
      title: {
        english: 'Ethers — Preparation & Reactions',
        hindi: 'ईथर — तैयारी और अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Williamson synthesis: $R\\text{-O}^-Na^+ + R\\text{\'-X} \\to R\\text{-O-R\'} + NaX$. Best with **1° R\'-X (SN2)**; 3° gives elimination → alkene.',
        hindi: 'विलियमसन संश्लेषण: $R\\text{-O}^-Na^+ + R\\text{\'-X} \\to R\\text{-O-R\'} + NaX$। **1° R\'-X (SN2)** के साथ सर्वश्रेष्ठ; 3° उन्मूलन → एल्कीन देता है।',
            },
            {
              english: 'For aryl alkyl ether: take phenoxide + alkyl halide (not aryl halide).',
        hindi: 'एरिल एल्काइल ईथर के लिए: फिनोक्साइड + एल्काइल हैलाइड (एरिल हैलाइड नहीं) लें।',
            },
            {
              english: 'Acid-catalysed dehydration of 1° alcohol (140°C with H₂SO₄) → symmetrical ether.',
        hindi: '1° अल्कोहल का एसिड-उत्प्रेरित निर्जलीकरण (H₂SO₄ के साथ 140°C) → सममित ईथर।',
            },
            {
              english: 'Reactions:',
        hindi: 'प्रतिक्रियाएं:',
            },
            {
              english: '• Cleavage by HX (HI > HBr > HCl): $R\\text{-O-R\'} + HI \\to R\\text{-I} + R\\text{\'-OH}$. With excess HI, alcohol also converts to halide.',
        hindi: '• HX द्वारा दरार (HI > HBr > HCl): $R\\text{-O-R\'} + HI \\to R\\text{-I} + R\\text{\'-OH}$। HI की अधिकता से अल्कोहल भी हैलाइड में परिवर्तित हो जाता है।',
            },
            {
              english: '• In **mixed alkyl ether**, smaller (less hindered) alkyl group leaves with I (SN2). For tert-alkyl ether, t-alkyl gives t-alkyl iodide via SN1.',
        hindi: '• **मिश्रित एल्काइल ईथर** में, छोटा (कम बाधित) एल्काइल समूह I (SN2) के साथ निकलता है। टर्ट-एल्काइल ईथर के लिए, t-एल्काइल SN1 के माध्यम से t-एल्काइल आयोडाइड देता है।',
            },
            {
              english: '• **Aryl alkyl ether**: HI cleaves O–alkyl bond (alkyl iodide + phenol; aryl-O bond stronger).',
        hindi: '• **एरिल एल्काइल ईथर**: HI ओ-एल्काइल बॉन्ड (एल्काइल आयोडाइड + फिनोल; एरिल-ओ बॉन्ड मजबूत) को तोड़ता है।',
            },
            {
              english: '• Electrophilic substitution on anisole (PhOCH₃): –OR is activating, ortho/para directing.',
        hindi: '• एनीसोल (PhOCH₃) पर इलेक्ट्रोफिलिक प्रतिस्थापन: -OR सक्रिय हो रहा है, ortho/para निर्देशित कर रहा है।',
            },
            {
              english: '• Combustion / peroxide formation: ethers form explosive peroxides on long air exposure.',
        hindi: '• दहन / पेरोक्साइड निर्माण: लंबे समय तक हवा में रहने पर ईथर विस्फोटक पेरोक्साइड बनाते हैं।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'C_6H_5CH(CH_3)_2 \\xrightarrow{O_2,H^+} C_6H_5OH + (CH_3)_2CO',
      desc: {
        english: 'Cumene process',
        hindi: 'क्यूमीन प्रक्रिया',
      },
    },
    {
      latex: 'C_6H_5O^-Na^+ + CO_2 \\xrightarrow{400\\,K, H^+} \\text{salicylic acid}',
      desc: {
        english: 'Kolbe-Schmitt',
        hindi: 'कोल्बे-श्मिट',
      },
    },
    {
      latex: 'C_6H_5OH + CHCl_3 + NaOH \\to o\\text{-OHC-}C_6H_4OH',
      desc: {
        english: 'Reimer-Tiemann',
        hindi: 'रीमर-टीमैन',
      },
    },
    {
      latex: 'R\\text{-OH} + R\\text{\'COOH} \\xrightleftharpoons{H^+} R\\text{\'COOR} + H_2O',
      desc: {
        english: 'Esterification',
        hindi: 'एस्टेरिफिकेशन',
      },
    },
    {
      latex: '\\text{Acidity: phenol} \\gg \\text{alcohol}',
      desc: {
        english: 'Resonance in phenoxide',
        hindi: 'फ़ीनॉक्साइड में अनुनाद',
      },
    },
    {
      latex: 'BH_3,\\ then\\ H_2O_2/OH^-',
      desc: {
        english: 'Anti-Markovnikov hydration',
        hindi: 'एंटी-मार्कोवनिकोव हाइड्रेशन',
      },
    },
    {
      latex: 'R\\text{-O-R\'} + HI \\to R\\text{-I} + R\\text{\'-OH}',
      desc: {
        english: 'Ether cleavage',
        hindi: 'ईथर दरार',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Acidity',
      problem: {
        english: 'Most acidic phenol:',
        hindi: 'सर्वाधिक अम्लीय फिनोल:',
      },
      answer: {
        english: '2,4,6-Trinitrophenol (picric acid)',
        hindi: '2,4,6-ट्रिनिट्रोफेनॉल (पिक्रिक एसिड)',
      },
      solution: {
        english: 'Three EWG NO₂ stabilise phenoxide.',
        hindi: 'तीन EWG NO₂ फेनोक्साइड को स्थिर करते हैं।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Hydration',
      problem: {
        english: 'Reagent giving anti-Markovnikov OH addition to propene:',
        hindi: 'अभिकर्मक प्रोपेन में एंटी-मार्कोवनिकोव OH जोड़ देता है:',
      },
      answer: {
        english: 'B₂H₆ then H₂O₂/OH⁻',
        hindi: 'B₂H₆ फिर H₂O₂/OH⁻',
      },
      solution: {
        english: 'Hydroboration-oxidation.',
        hindi: 'हाइड्रोबोरेशन-ऑक्सीकरण।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Lucas',
      problem: {
        english: 'Alcohol giving cloudiness immediately with Lucas reagent:',
        hindi: 'लुकास अभिकर्मक के साथ अल्कोहल तुरंत बादल छा जाता है:',
      },
      answer: {
        english: 'tert-Butyl alcohol',
        hindi: 'टर्ट-ब्यूटाइल अल्कोहल',
      },
      solution: {
        english: '3° forms carbocation fastest.',
        hindi: '3° सबसे तेजी से कार्बोकेशन बनाता है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Williamson',
      problem: {
        english: 'Best route to t-butyl methyl ether (MTBE):',
        hindi: 'टी-ब्यूटाइल मिथाइल ईथर (MTBE) का सर्वोत्तम मार्ग:',
      },
      answer: {
        english: 't-BuO⁻ + CH₃I',
        hindi: 't-BuO⁻ + CH₃I',
      },
      solution: {
        english: 'Use bulky alkoxide + small 1° halide to avoid elimination.',
        hindi: 'उन्मूलन से बचने के लिए भारी एल्कोऑक्साइड + छोटे 1° हैलाइड का उपयोग करें।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Reaction',
      problem: {
        english: 'Phenol + CHCl₃ + NaOH gives:',
        hindi: 'फिनोल + CHCl₃ + NaOH देता है:',
      },
      answer: {
        english: 'Salicylaldehyde',
        hindi: 'सैलिसिलैल्डिहाइड',
      },
      solution: {
        english: 'Reimer-Tiemann.',
        hindi: 'रीमर-टीमैन।',
      },
    },
  ],
  next: {
    slug: 'aldehydes-ketones-acids',
    title: 'Aldehydes, Ketones & Carboxylic Acids',
  },
};

export default chapter;
